import React from "react";
import fetchJsonp from "fetch-jsonp";
import qs from "query-string";
import { Redirect } from "react-router-dom";

import ApplicationLayout, { applicationStyles as styles } from "../ApplicationLayout";
import WithUserForm from "../../Register/WithUserForm";
import CheckingInstagram from "../CheckingInstagram";

const redirectUri = `${window.location.origin}/apply/instagram`.replace("http://", "https://");

const CONNECT_STEPS = [
  {
    title: "Use a Creator or Business account",
    text: "Personal and private accounts can't connect. If yours is personal, Instagram will offer to switch it. It's free and you can switch back later.",
  },
  {
    title: "Allow access to your photos",
    text: (
      <>
        We only see what's already public on your profile.{" "}
        <a href="https://help.instagram.com/581066165581870" target="_blank" rel="noopener noreferrer">
          Instagram's terms
        </a>
      </>
    ),
  },
  {
    title: "Pick 6 photos",
    text: "Back here, you'll choose the 6 that best show you and your work.",
  },
];


const returningFromInstagram = () => window.location.search.substr(1).startsWith("code");

const startInstagramOAuth = () => {
  const query = qs.stringify({
    client_id: process.env.REACT_APP_SCHMODEL_INSTAGRAM_APP_ID,
    redirect_uri: redirectUri,
    scope: "instagram_business_basic",
    response_type: "code",
    state: window.localStorage.getItem("token"),
  });
  window.location.href = `https://www.instagram.com/oauth/authorize?${query}`;
};

class InstagramConnect extends React.Component {
  state = {
    showVerifyModal: false,
    hasMediaAccess: false,
    accessToken: null,
  };

  componentDidMount() {
    this.getAuthenticateToken();
    this.getAccessToken();
  }

  showVerifyModal = () => {
    this.setState({ showVerifyModal: true });
    setTimeout(() => {
      this.waitFor((_) => this.state.hasMediaAccess).then((_) =>
        this.setState({ showVerifyModal: false })
      );
    }, 2000);
  };

  waitFor(conditionFunction) {
    const poll = (resolve) => {
      if (conditionFunction()) resolve();
      else setTimeout((_) => poll(resolve), 300);
    };
    return new Promise(poll);
  }

  getAccessToken = async () => {
    const hashString = window.location.search.substr(1, window.location.search.indexOf("&state") - 1);
    if (hashString.startsWith("code")) {
      this.showVerifyModal();
      const accessCode = hashString.substr(5);
      try {
        const response = await this.props.exchangeToken({
          variables: { code: accessCode, redirectUri },
        });
        let userAccessToken = response.data.exchangeToken;
        // Refetch current user to ensure instagramToken is available before proceeding to photo selection
        await this.props.userQuery.refetchCurrentUser();
        this.setState({ accessToken: userAccessToken });
        this.checkMediaAccess();
      } catch (error) {
        console.log(error);
      }
    }
  };

  checkMediaAccess = async () => {
    const queryString = qs.stringify({ access_token: this.state.accessToken });
    const body = await fetchJsonp("https://graph.instagram.com/v21.0/me/media/?" + queryString)
      .then((res) => res.json())
      .catch((error) => console.log(error));

    if (!body.hasOwnProperty("error")) {
      this.setState({ hasMediaAccess: true });
    }
  };

  getAuthenticateToken = () => {
    const hashString = window.location.search.substr(window.location.search.indexOf("state="));
    const authToken = hashString.substr(6);

    if (!window.localStorage.getItem("token")) {
      window.localStorage.setItem("token", authToken);
    }
  };

  renderFailed() {
    const footer = (
      <div className={styles.actions}>
        <button type="button" className={styles.cta} onClick={startInstagramOAuth}>
          Try again
        </button>
        <p className={styles.footerNote}>
          Still stuck? <a href="https://wa.me/443330166335">Contact us</a>
        </p>
      </div>
    );

    return (
      <ApplicationLayout step={5} footer={footer}>
        <div>
          <div className={styles.errorIcon}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M11 6v6" />
              <path d="M11 16h.01" />
            </svg>
          </div>
          <h1 className={styles.title} style={{ marginTop: 20 }}>Instagram didn't connect</h1>
          <p className={styles.intro}>
            Nothing's lost: your answers so far are saved. This usually happens for one of two reasons:
          </p>
          <ul className={styles.bullets}>
            <li>You tapped <strong>Cancel</strong> or <strong>Not now</strong> instead of <strong>Allow</strong> when Instagram asked.</li>
            <li>The account is personal or private. It needs to be a Creator or Business account.</li>
          </ul>
        </div>
      </ApplicationLayout>
    );
  }

  render() {
    const { machine } = this.props;
    const search = window.location.search.substr(1);
    const onBack = () => machine.service.send("BACK");

    if (!this.state.showVerifyModal && this.state.hasMediaAccess) {
      return <Redirect to="/apply/photos" />;
    }

    if (this.state.showVerifyModal) return <CheckingInstagram onRetry={startInstagramOAuth} />;
    if (search.startsWith("error")) return this.renderFailed();

    const footer = (
      <div className={styles.actions}>
        <button type="button" className={styles.cta} onClick={startInstagramOAuth}>
          Connect Instagram
        </button>
        <p className={styles.footerNote}>
          Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
        </p>
      </div>
    );

    return (
      <ApplicationLayout
        step={5}
        onBack={onBack}
        title="Connect your Instagram"
        intro="We use it to confirm who you are and to bring in the photos for your profile. It takes about a minute."
        footer={footer}
      >
        <div className={styles.steps}>
          {CONNECT_STEPS.map((step, i) => (
            <div key={step.title} className={styles.stepItem}>
              <div className={styles.stepNumber}>{i + 1}</div>
              <div className={styles.stepBody}>
                <div className={styles.stepTitle}>{step.title}</div>
                <div className={styles.stepText}>{step.text}</div>
              </div>
            </div>
          ))}
        </div>
      </ApplicationLayout>
    );
  }
}

export default class AccessInstagram extends React.Component {
  render() {
    return (
      <WithUserForm
        customLoading={returningFromInstagram() ? () => <CheckingInstagram onRetry={startInstagramOAuth} /> : undefined}
        render={(props) => <InstagramConnect {...props} />}
      />
    );
  }
}
